/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { PROFILE_FORM_SCHEMA } from './schema';
import { toaster } from '../../components/ui/toaster';

// --- Types ---

type ValidationResult = {
  isValid: boolean;
  errors: Record<string, string>;
};

// --- Helpers ---

const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const getFieldLabel = (fieldId: string): string => {
  for (const section of PROFILE_FORM_SCHEMA) {
    const param = section.parameters.find((p) => p.id === fieldId);
    if (param) return param.label;
  }
  return fieldId;
};

// --- Generic Validators ---

/**
 * Generic validator for name fields (first name, last name)
 * Validates: required, min 2 chars, max 50 chars, letters and spaces only
 */
const validateNameField = (value: unknown, fieldLabel: string): string | null => {
  if (isEmpty(value)) return `${fieldLabel} is required`;
  const str = String(value);
  if (str.length < 2) return `${fieldLabel} must be at least 2 characters`;
  if (str.length > 50) return `${fieldLabel} must be at most 50 characters`;
  if (!/^[a-zA-Z\s]+$/.test(str)) return `${fieldLabel} contains invalid characters (only letters and spaces allowed)`;
  return null;
};

/**
 * Generic validator for college/university fields
 * Validates: max 200 characters (if provided)
 */
const validateCollegeField = (value: unknown, fieldLabel: string): string | null => {
  if (!isEmpty(value) && String(value).length > 200) return `${fieldLabel} must be at most 200 characters`;
  return null;
};

/**
 * Generic validator for year fields
 * Validates: 4-digit year format (if provided)
 */
const validateYearField = (value: unknown, fieldLabel: string): string | null => {
  if (isEmpty(value)) return null;
  const str = String(value);
  if (!/^[0-9]{4}$/.test(str)) return `${fieldLabel} must be a 4-digit year (e.g. 2020)`;
  return null;
};

// --- Individual Field Validators ---

export const validateFirstName = (value: unknown): string | null => {
  return validateNameField(value, 'First name');
};

export const validateLastName = (value: unknown): string | null => {
  return validateNameField(value, 'Last name');
};

export const validateEmail = (value: unknown): string | null => {
  if (isEmpty(value)) return 'Email is required';
  const str = String(value);
  if (str.length > 255) return 'Email must be at most 255 characters';
  // Simple email regex, can be more strict if needed but should match basic format
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) return 'Email is invalid';
  return null;
};

export const validateDateOfBirth = (value: unknown): string | null => {
  if (isEmpty(value)) return 'Date of birth is required';

  const birthDate = new Date(value as string);
  if (isNaN(birthDate.getTime())) return 'Date of birth is invalid';

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  if (age < 18) return 'Date of birth must indicate an age of at least 18 years';

  return null;
};

export const validateGender = (value: unknown): string | null => {
  if (isEmpty(value)) return 'Gender is required';
  return null;
};

export const validatePincode = (value: unknown): string | null => {
  if (isEmpty(value)) return 'Pincode is required';
  const str = String(value);
  if (!/^[0-9]{6}$/.test(str)) return 'Pincode must be exactly 6 digits';
  return null;
};

export const validateProfession = (value: unknown): string | null => {
  if (isEmpty(value)) return 'Profession is required';
  return null;
};

export const validateLanguagesWrittenProficiency = (value: unknown): string | null => {
  if (isEmpty(value)) return 'Languages (Written Proficiency) requires at least one selection';
  if (!Array.isArray(value) || value.length === 0)
    return 'Languages (Written Proficiency) requires at least one selection';
  return null;
};

export const validateLinkedInProfile = (value: unknown): string | null => {
  if (isEmpty(value)) return 'LinkedIn profile is required';
  const str = String(value);
  if (str.length > 200) return 'LinkedIn profile must be at most 200 characters';
  // Use the same regex as in schema
  if (!/^https?:\/\/(www\.)?linkedin\.com\/in\/([a-zA-Z0-9_-]|%[0-9A-Fa-f]{2})+\/?$/.test(str)) {
    return 'LinkedIn profile URL is invalid';
  }
  return null;
};

// Education fields are optional, but if partially filled, might need validation logic.
// However, schema says they are optional. We'll check for consistency if needed.
// For now, we'll validate format if value is present.

export const validateLatestDegree = (_value: unknown): string | null => {
  // Optional
  return null;
};

export const validateLatestDegreeCollege = (value: unknown): string | null => {
  return validateCollegeField(value, 'College/University');
};

export const validateLatestDegreeYear = (value: unknown): string | null => {
  return validateYearField(value, 'Year of graduation');
};

export const validatePreviousDegree = (_value: unknown): string | null => {
  return null;
};

export const validatePreviousDegreeCollege = (value: unknown): string | null => {
  return validateCollegeField(value, 'College/University');
};

export const validatePreviousDegreeYear = (value: unknown): string | null => {
  return validateYearField(value, 'Year of graduation');
};

export const validateResumeCv = (value: unknown): string | null => {
  if (!value) return null; // Optional

  if (!(value instanceof File)) return 'Resume/CV file is invalid';

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  if (!ALLOWED_MIME_TYPES.includes(value.type)) {
    return 'Resume/CV file type is invalid. Please upload a PDF or DOCX file.';
  }

  if (value.size > MAX_FILE_SIZE) {
    return 'Resume/CV file size exceeds 5MB limit';
  }

  return null;
};

// --- Orchestrator ---

export const validateProfileForm = (data: Record<string, unknown>): ValidationResult => {
  const errors: Record<string, string> = {};

  const addError = (field: string, error: string | null) => {
    if (error) errors[field] = error;
  };

  addError('first_name', validateFirstName(data.first_name));
  addError('last_name', validateLastName(data.last_name));
  addError('email', validateEmail(data.email));
  addError('date_of_birth', validateDateOfBirth(data.date_of_birth));
  addError('gender', validateGender(data.gender));
  addError('pincode', validatePincode(data.pincode));
  addError('profession', validateProfession(data.profession));
  addError('languages_written_proficiency', validateLanguagesWrittenProficiency(data.languages_written_proficiency));
  addError('linkedin_profile', validateLinkedInProfile(data.linkedin_profile));

  // Education validation - if degree is selected, all fields must be filled
  const hasLatestDegree = !isEmpty(data.latest_degree);
  const hasPreviousDegree = !isEmpty(data.previous_degree);

  if (hasLatestDegree) {
    // If latest degree is selected, college and year are required
    if (isEmpty(data.latest_degree_college)) {
      addError('latest_degree_college', 'College/University is required when latest degree is selected');
    } else {
      addError('latest_degree_college', validateLatestDegreeCollege(data.latest_degree_college));
    }

    if (isEmpty(data.latest_degree_year)) {
      addError('latest_degree_year', 'Year of graduation is required when latest degree is selected');
    } else {
      addError('latest_degree_year', validateLatestDegreeYear(data.latest_degree_year));
    }
  } else {
    // If no degree selected, validate individual fields if they have values
    addError('latest_degree_college', validateLatestDegreeCollege(data.latest_degree_college));
    addError('latest_degree_year', validateLatestDegreeYear(data.latest_degree_year));
  }

  if (hasPreviousDegree) {
    // If previous degree is selected, college and year are required
    if (isEmpty(data.previous_degree_college)) {
      addError('previous_degree_college', 'College/University is required when previous degree is selected');
    } else {
      addError('previous_degree_college', validatePreviousDegreeCollege(data.previous_degree_college));
    }

    if (isEmpty(data.previous_degree_year)) {
      addError('previous_degree_year', 'Year of graduation is required when previous degree is selected');
    } else {
      addError('previous_degree_year', validatePreviousDegreeYear(data.previous_degree_year));
    }
  } else {
    // If no degree selected, validate individual fields if they have values
    addError('previous_degree_college', validatePreviousDegreeCollege(data.previous_degree_college));
    addError('previous_degree_year', validatePreviousDegreeYear(data.previous_degree_year));
  }

  addError('resume_cv', validateResumeCv(data.resume_cv));

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// --- UI Helper ---

export const showValidationErrorToasts = (errors: Record<string, string>) => {
  const errorEntries = Object.entries(errors);
  const count = errorEntries.length;

  if (count === 0) return;

  if (count === 1) {
    const [fieldId, message] = errorEntries[0];
    const label = getFieldLabel(fieldId);

    toaster.create({
      title: 'Validation Error',
      description: `${label}: ${message}`,
      type: 'error',
      duration: 5000,
    });
  } else {
    // Show summary for multiple errors
    const firstFewErrors = errorEntries.slice(0, 3).map(([fieldId]) => {
      const label = getFieldLabel(fieldId);
      return `• ${label}`;
    });

    const description = firstFewErrors.join('\n') + (count > 3 ? `\n...and ${count - 3} more fields.` : '');

    toaster.create({
      title: `Please check the following fields:`,
      description: description,
      type: 'error',
      duration: 6000,
    });
  }
};
